import { useState } from 'react';
import PhoneInput, { isValidPhone } from '@/components/common/PhoneInput';
import { Navigate, Link } from 'react-router-dom';
import { useDashboard } from '@/context/DashboardContext';
import { currentLang, useSiteLang } from '@/components/site/SiteLang';
import DashLangSwitch from '@/components/dashboard/DashLangSwitch';
import { BACKEND_URL } from '@/config/api';
import { UserPlus, Loader2, Eye, EyeOff, UserCheck } from 'lucide-react';
import { motion } from 'framer-motion';
import { useReferral } from '@/hooks/useReferral';
import { useFormToken } from '@/hooks/useFormToken';

/*
  Defensa frente a bots (23/10/2026, backend/antibot.py):
  - Al abrir el formulario se pide una ficha firmada al servidor; el registro solo se acepta a partir de 3 s.
  - Campo trampa «website», invisible para las personas.
  La ficha la da hooks/useFormToken (la misma para contacto y test de idoneidad).
*/
const T = {
  es: {
    errName: 'Indica tu nombre y apellidos',
    errPhone: 'Revisa el número de teléfono',
    errLen: 'La contraseña debe tener al menos 8 caracteres',
    errUpper: 'La contraseña debe incluir al menos una mayúscula',
    errDigit: 'La contraseña debe incluir al menos un número',
    errMatch: 'Las contraseñas no coinciden',
    errNetwork: 'No hemos podido conectar con el servidor. Inténtalo de nuevo en unos segundos.',
    err422: 'Revisa los datos del formulario',
    errGeneric: 'Error al registrar',
    homeAria: 'Volver a la web de Freedom Homes Club',
    title: 'Crear cuenta',
    subtitle: 'Únete a Freedom Homes Club',
    invitedBy: 'Has sido invitado por',
    name: 'Nombre completo',
    namePh: 'Tu nombre y apellidos',
    email: 'Email',
    emailPh: 'tu@email.com',
    phone: 'Teléfono',
    password: 'Contraseña',
    passwordPh: 'Mínimo 8 caracteres, una mayúscula y un número',
    confirm: 'Confirmar contraseña',
    confirmPh: 'Repite tu contraseña',
    hide: 'Ocultar contraseña',
    show: 'Mostrar contraseña',
    loading: 'Registrando...',
    submit: 'Crear cuenta',
    haveAccount: '¿Ya tienes cuenta?',
    login: 'Iniciar sesión',
  },
  en: {
    errName: 'Please enter your first name and surname',
    errPhone: 'Please check your phone number',
    errLen: 'Your password must be at least 8 characters long',
    errUpper: 'Your password must include at least one capital letter',
    errDigit: 'Your password must include at least one number',
    errMatch: 'The passwords don’t match',
    errNetwork: 'We couldn’t reach the server. Please try again in a few seconds.',
    err422: 'Please check the details in the form',
    errGeneric: 'We couldn’t create your account',
    homeAria: 'Back to the Freedom Homes Club website',
    title: 'Create account',
    subtitle: 'Join Freedom Homes Club',
    invitedBy: 'You’ve been invited by',
    name: 'Full name',
    namePh: 'Your first name and surname',
    email: 'Email',
    emailPh: 'you@email.com',
    phone: 'Phone',
    password: 'Password',
    passwordPh: 'At least 8 characters, one capital letter and one number',
    confirm: 'Confirm password',
    confirmPh: 'Repeat your password',
    hide: 'Hide password',
    show: 'Show password',
    loading: 'Creating account…',
    submit: 'Create account',
    haveAccount: 'Already have an account?',
    login: 'Sign in',
  },
};

export default function DashboardRegister() {
  const [form, setForm] = useState({ name: '', email: '', password: '', confirmPassword: '', phone: '', website: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { user } = useDashboard();
  const { referralCode, affiliateName } = useReferral();
  const formToken = useFormToken();
  const { lang } = useSiteLang();
  const t = T[lang] || T.es;

  if (user) return <Navigate to="/dashboard" replace />;

  const set = (key) => (e) => setForm(prev => ({ ...prev, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.name.trim().split(/\s+/).length < 2) { setError(t.errName); return; }
    if (!isValidPhone(form.phone)) { setError(t.errPhone); return; }
    if (form.password.length < 8) { setError(t.errLen); return; }
    if (!/[A-Z]/.test(form.password)) { setError(t.errUpper); return; }
    if (!/[0-9]/.test(form.password)) { setError(t.errDigit); return; }
    if (form.password !== form.confirmPassword) { setError(t.errMatch); return; }
    setLoading(true);
    let res;
    try {
      const token = await formToken();
      res = await fetch(`${BACKEND_URL}/api/dashboard/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Lang': currentLang() },
        body: JSON.stringify({ name: form.name, email: form.email, password: form.password, phone: form.phone, referral_code: referralCode || undefined,
          form_token: token || undefined, website: form.website || undefined, lang: currentLang() }),
      });
    } catch {
      setError(t.errNetwork);
      setLoading(false);
      return;
    }
    try {
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        const d = err.detail;
        // Los 422 de validación traen un array: se muestra un mensaje legible
        throw new Error(typeof d === 'string' ? d : (res.status === 422 ? t.err422 : t.errGeneric));
      }
      const data = await res.json();
      sessionStorage.setItem('dashboard_token', data.token);
      sessionStorage.setItem('dashboard_user', JSON.stringify(data.user));
      // Solo en el entorno de pruebas sin servidor de correo: el backend devuelve el código para poder probar
      if (data.dev_code) sessionStorage.setItem('fhc_dev_code', data.dev_code);
      window.location.href = '/dashboard';
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const inputClass = 'w-full bg-white/[0.03] border border-white/[0.06] rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-[#c9a84c]/30 transition-colors disabled:opacity-50 placeholder:text-gray-500';

  return (
    <div className="min-h-screen bg-[#060d1b] flex items-center justify-center px-4 py-12 relative overflow-hidden">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/3 -right-32 w-96 h-96 bg-[#c9a84c]/[0.03] rounded-full blur-3xl" />
        <div className="absolute bottom-1/3 -left-32 w-96 h-96 bg-[#c9a84c]/[0.02] rounded-full blur-3xl" />
      </div>

      <div className="fixed top-3 right-3 z-20"><DashLangSwitch /></div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md relative z-10"
      >
        <div className="text-center mb-8">
          <Link to="/" className="inline-block mb-6 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-[#c9a84c]/60" aria-label={t.homeAria} data-testid="register-logo-home">
            <img src="/logo-fhc-sm.png" alt="Freedom Homes Club" className="h-16 w-auto mx-auto" />
          </Link>
          <h1 className="text-2xl font-bold text-white mb-2 tracking-tight" data-testid="register-title">{t.title}</h1>
          <p className="text-gray-400 text-sm">{t.subtitle}</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white/[0.02] backdrop-blur-xl border border-white/[0.06] rounded-2xl p-8 space-y-4" data-testid="dashboard-register-form">
          {affiliateName && (
            <div className="flex items-center gap-3 bg-[#c9a84c]/[0.08] border border-[#c9a84c]/25 rounded-xl px-4 py-3" data-testid="register-referral-badge">
              <UserCheck size={18} className="text-[#c9a84c] flex-shrink-0" />
              <p className="text-sm text-gray-300">{t.invitedBy} <strong className="text-[#c9a84c]">{affiliateName}</strong></p>
            </div>
          )}
          <div>
            <label className="block text-[11px] text-gray-400 uppercase tracking-wider font-medium mb-2">{t.name}</label>
            <input type="text" value={form.name} onChange={set('name')} required disabled={loading} className={inputClass} placeholder={t.namePh} autoComplete="name" data-testid="register-name" />
          </div>
          {/* Campo trampa: invisible y fuera del tabulador; solo lo rellena un bot */}
          <div aria-hidden="true" style={{ position: 'absolute', left: '-10000px', width: 1, height: 1, overflow: 'hidden' }}>
            <label>Web <input type="text" name="website" tabIndex={-1} autoComplete="off" value={form.website} onChange={set('website')} /></label>
          </div>
          <div>
            <label className="block text-[11px] text-gray-400 uppercase tracking-wider font-medium mb-2">{t.email}</label>
            <input type="email" value={form.email} onChange={set('email')} required disabled={loading} className={inputClass} placeholder={t.emailPh} data-testid="register-email" />
          </div>
          <div>
            <label className="block text-[11px] text-gray-400 uppercase tracking-wider font-medium mb-2">{t.phone}</label>
            <PhoneInput value={form.phone} onChange={(v) => setForm((prev) => ({ ...prev, phone: v }))} variant="dark" rounded="rounded-xl" heightClass="h-[46px]" required disabled={loading} testId="register-phone" />
          </div>
          <div>
            <label className="block text-[11px] text-gray-400 uppercase tracking-wider font-medium mb-2">{t.password}</label>
            <div className="relative">
              <input type={showPassword ? 'text' : 'password'} value={form.password} onChange={set('password')} required disabled={loading} className={inputClass} placeholder={t.passwordPh} data-testid="register-password" />
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-400 transition-colors" aria-label={showPassword ? t.hide : t.show} data-testid="register-password-toggle">
                {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
          </div>
          <div>
            <label className="block text-[11px] text-gray-400 uppercase tracking-wider font-medium mb-2">{t.confirm}</label>
            <div className="relative">
              <input type={showConfirmPassword ? 'text' : 'password'} value={form.confirmPassword} onChange={set('confirmPassword')} required disabled={loading} className={inputClass} placeholder={t.confirmPh} data-testid="register-confirm-password" />
              <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-400 transition-colors" data-testid="register-confirm-password-toggle" aria-label={showConfirmPassword ? t.hide : t.show}>
                {showConfirmPassword ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
          </div>
          {error && <p className="text-red-400 text-sm" data-testid="register-error">{error}</p>}
          <button type="submit" disabled={loading} className="w-full bg-[#c9a84c] hover:bg-[#b8973f] text-[#0a1628] font-semibold py-3 rounded-xl transition-all flex items-center justify-center gap-2 disabled:opacity-50 shadow-lg shadow-[#c9a84c]/15" data-testid="register-submit">
            {loading ? <Loader2 size={16} className="animate-spin" /> : <UserPlus size={15} />}
            {loading ? t.loading : t.submit}
          </button>
          <p className="text-center text-sm text-gray-400 mt-3">
            {t.haveAccount}{' '}
            <a href="/dashboard/login" className="text-[#c9a84c] hover:underline" data-testid="register-login-link">{t.login}</a>
          </p>
        </form>
      </motion.div>
    </div>
  );
}
